import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Center,
  Spinner,
  Alert,
  AlertIcon,
  useDisclosure,
  useToast,
  Badge,
  Tooltip,
  Spacer,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import {
  SearchIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import { FaSortUp, FaSortDown, FaEnvelope, FaPencilAlt, FaArrowRight, FaQuestionCircle } from 'react-icons/fa';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';
import { useMenuTitle } from '../hooks/useMenuTitle';
import { useInfiniteQuery } from '@tanstack/react-query';

const ITEMS_PER_PAGE = 50;

const statusColors = {
  PENDING: 'yellow',
  CONSENTED: 'green',
  REVOKED: 'red'
};

const actionColors = {
  REQUEST_SENT: 'blue',
  FORM_COMPLETED: 'green',
  ADMIN_UPDATE: 'purple',
  REVOKED: 'red'
};

const changedByTypeColors = {
  SYSTEM: 'gray',
  ADMIN: 'purple',
  MEMBER_FORM: 'blue',
  MEMBER_TEXT: 'teal'
};

const ConsentHistory = () => {
  const menuTitle = useMenuTitle();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedChangedByType, setSelectedChangedByType] = useState('');
  const [sortField, setSortField] = useState('changed_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLog, setSelectedLog] = useState(null);
  const observer = useRef();
  const [selectedContactId, setSelectedContactId] = useState(null);
  const toast = useToast();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const fetchConsentLogs = async ({ pageParam = 0 }) => {
    let query = supabase
      .from('consent_history_view')
      .select(`
        id,
        changed_at,
        contact_id,
        contact_name,
        contact_email,
        contact_phone,
        action,
        previous_status,
        new_status,
        changed_by_type,
        admin_name,
        admin_email
      `)
      .order('changed_at', { ascending: false })
      .range(pageParam, pageParam + ITEMS_PER_PAGE - 1);

    if (searchQuery) {
      query = query.or(`contact_name.ilike.%${searchQuery}%,contact_email.ilike.%${searchQuery}%,contact_phone.ilike.%${searchQuery}%`);
    }
    
    if (selectedAction) {
      query = query.eq('action', selectedAction);
    }
    
    if (selectedStatus) {
      query = query.eq('new_status', selectedStatus);
    }
    
    if (selectedChangedByType) {
      query = query.eq('changed_by_type', selectedChangedByType);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return data;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error
  } = useInfiniteQuery({
    queryKey: ['consentLogs', searchQuery, selectedAction, selectedStatus, selectedChangedByType],
    queryFn: fetchConsentLogs,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage?.length < ITEMS_PER_PAGE) return undefined;
      return pages.length * ITEMS_PER_PAGE;
    }
  });

  const allLogs = useMemo(() => {
    const logs = data?.pages.flatMap(page => page) ?? [];
    return [...logs].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      const comparison = typeof aValue === 'string' 
        ? aValue.localeCompare(bValue)
        : aValue - bValue;
        
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, sortField, sortOrder]);

  const lastRowRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    if (isFetchingNextPage) return;

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    });

    if (node) observer.current.observe(node);
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const handleResendWelcomeEmail = async (contactId) => {
    try {
      const { error } = await supabase.rpc('resend_welcome_email', { contact_id: contactId });
      if (error) throw error;
      toast({
        title: 'Welcome email sent',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error sending welcome email',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleChangeStatus = (contactId) => {
    setSelectedContactId(contactId);
    onChangeStatusOpen();
  };

  if (status === 'error') {
    return (
      <Alert status="error">
        <AlertIcon />
        Error loading consent history: {error.message}
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Flex alignItems="center" mb={4}>
        <Heading size="md">{menuTitle}</Heading>
        <Spacer />
        <Popover placement="left">
          <PopoverTrigger>
            <IconButton
              icon={<FaQuestionCircle />}
              variant="ghost"
              aria-label="Help"
            />
          </PopoverTrigger>
          <PopoverContent p={4} maxW="400px">
            <PopoverArrow />
            <PopoverBody>
              <VStack align="stretch" spacing={3}>
                <Text fontWeight="bold">About Consent History</Text>
                <Text>
                  This page shows a complete history of all consent status changes, including:
                </Text>
                <UnorderedList pl={4} spacing={2}>
                  <ListItem>Manual changes by administrators</ListItem>
                  <ListItem>SMS opt-in/opt-out responses</ListItem>
                  <ListItem>Changes from bulk imports</ListItem>
                </UnorderedList>
                <Text>
                  Use the search and filters to find specific consent changes. Click the column headers to sort.
                </Text>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>

      <Flex gap={2} alignItems="center" mb={4}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>

        <Select
          value={selectedAction}
          onChange={(e) => setSelectedAction(e.target.value)}
          maxW="200px"
          placeholder="Filter by action"
        >
          <option value="MANUAL">Manual</option>
          <option value="SMS">SMS</option>
          <option value="IMPORT">Import</option>
        </Select>

        <Select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          maxW="200px"
          placeholder="Filter by status"
        >
          <option value="OPTED_IN">Opted In</option>
          <option value="OPTED_OUT">Opted Out</option>
          <option value="UNKNOWN">Unknown</option>
        </Select>

        <Select
          value={selectedChangedByType}
          onChange={(e) => setSelectedChangedByType(e.target.value)}
          maxW="200px"
          placeholder="Filter by changed by"
        >
          <option value="ADMIN">Admin</option>
          <option value="CONTACT">Contact</option>
          <option value="SYSTEM">System</option>
        </Select>
      </Flex>

      <Box overflowX="auto">
        <Table size="sm" variant="simple" sx={{ 
          'td, th': {
            padding: '0px 4px'
          }
        }}>
          <Thead>
            <Tr>
              {[
                { id: 'changed_at', label: 'Date' },
                { id: 'contact_name', label: 'Name' },
                { id: 'contact_email', label: 'Email' },
                { id: 'contact_phone', label: 'Phone' },
                { id: 'action', label: 'Change Type' },
                { id: null, label: 'Status Change' },
                { id: 'changed_by_type', label: 'By' },
                { id: 'admin_name', label: 'Admin Name' },
                { id: null, label: 'Actions' }
              ].map(column => (
                <Th 
                  key={column.id || column.label}
                  cursor={column.id ? 'pointer' : 'default'}
                  onClick={column.id ? () => handleSort(column.id) : undefined}
                  color="gray.500"
                  fontWeight="medium"
                  textTransform="none"
                  fontSize="sm"
                >
                  <Flex alignItems="center" height="100%">
                    {column.label}
                    {column.id && (
                      <Box ml={1} display="flex" alignItems="center" height="100%">
                        {sortField === column.id ? (
                          <Icon 
                            as={sortOrder === 'asc' ? FaSortUp : FaSortDown}
                            color="gray.500"
                            boxSize={3}
                          />
                        ) : (
                          <Box position="relative" width="12px" height="16px">
                            <Icon 
                              as={FaSortUp} 
                              color="gray.400" 
                              boxSize={3}
                              position="absolute"
                              top="0"
                            />
                            <Icon 
                              as={FaSortDown} 
                              color="gray.400" 
                              boxSize={3}
                              position="absolute"
                              top="1px"
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {allLogs.map((log, index) => (
              <Tr 
                key={log.id}
                ref={index === allLogs.length - 1 ? lastRowRef : null}
              >
                <Td whiteSpace="nowrap">
                  {format(new Date(log.changed_at), 'yyyy-MM-dd HH:mm')}
                </Td>
                <Td>{log.contact_name}</Td>
                <Td>{log.contact_email}</Td>
                <Td>{log.contact_phone}</Td>
                <Td>
                  <Badge>
                    {log.action}
                  </Badge>
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <Badge colorScheme={statusColors[log.previous_status]}>
                      {log.previous_status}
                    </Badge>
                    <Icon as={FaArrowRight} color="gray.500" />
                    <Badge colorScheme={statusColors[log.new_status]}>
                      {log.new_status}
                    </Badge>
                  </HStack>
                </Td>
                <Td>
                  <Badge>
                    {log.changed_by_type}
                  </Badge>
                </Td>
                <Td>
                  {log.changed_by_type === 'ADMIN' && (log.admin_name || log.admin_email)}
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FaEnvelope />}
                      aria-label="Resend welcome email"
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleResendWelcomeEmail(log.contact_id)}
                    />
                    <IconButton
                      icon={<FaPencilAlt />}
                      aria-label="Change consent status"
                      size="xs"
                      variant="ghost"
                      onClick={() => handleChangeStatus(log.contact_id)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {isFetchingNextPage && (
        <Center p={4}>
          <Spinner />
        </Center>
      )}
    </VStack>
  );
};

export default ConsentHistory;
